import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { cn } from '@/design-system/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/design-system/table';
import { toast } from '@/design-system/toaster';

import {
  isAbortError,
  parseWarehouseError,
  warehouseApi,
} from '../api/warehouse.api';
import { fmtQty } from '../format';
import { fieldErrorsOf } from '../hooks/useServerErrors';
import type {
  GoodsIssueLine,
  GoodsIssueSource,
  UpdateGoodsIssueLinesPayload,
} from '../types';
import { ActionDialog, DisabledReason } from './dialogs';
import { TextInput } from './form';
import {
  MAX_EDIT_ROWS,
  batchLabelOf,
  candidateOf,
  checkRows,
  type EditRow,
  isDirty,
  locationLabelOf,
  mapServerErrors,
  newEditRow,
  NO_SOURCES,
  type OrderedSku,
  type RowErrorKey,
  type RowErrors,
  type RowField,
  rowsFromLines,
  type SourceKnowledge,
  sourceKey,
  toPayload,
  withQty,
  withSku,
  withStock,
  withStorage,
} from './pickingEdit';
import {
  OrderedSkuSelect,
  SourceBatchSelect,
  SourceStorageSelect,
} from './SourceSelects';
import { metaCls, nameCls, numCls } from './ui';

/* ---------------------------- candidate rows ----------------------------- */

const KEY_SEP = '\u0002';
const withSet = <T,>(set: ReadonlySet<T>, v: T): ReadonlySet<T> =>
  new Set(set).add(v);
const withoutSet = <T,>(set: ReadonlySet<T>, v: T): ReadonlySet<T> => {
  const next = new Set(set);
  next.delete(v);
  return next;
};

/**
 * What the table knows about the stock it may reserve (see
 * {@link SourceKnowledge}). Location and batch are server-searched pickers, so
 * the only thing fetched here is the stock of the location a starting line
 * points at (one request per SKU + location, once per edit session — for its
 * "available" and the "still there?" check); a batch the user picks is
 * remembered with the row the picker handed over. Each request is abortable: a
 * retry replaces it and leaving edit mode aborts them all.
 */
const useSourceLookups = (goodsIssueID: string, rows: readonly EditRow[]) => {
  const [candidates, setCandidates] = useState<SourceKnowledge['candidates']>(
    () => NO_SOURCES.candidates
  );
  const [lists, setLists] = useState<SourceKnowledge['lists']>(
    () => NO_SOURCES.lists
  );
  const [loading, setLoading] = useState<ReadonlySet<string>>(() => new Set());
  const [failed, setFailed] = useState<ReadonlyMap<string, string>>(
    () => new Map()
  );
  const [version, setVersion] = useState(0);
  const controllers = useRef(new Map<string, AbortController>());
  const requested = useRef(new Set<string>());

  const load = useCallback(
    (sku: string, storageID: string) => {
      const key = sourceKey(sku, storageID);
      requested.current.add(key);
      controllers.current.get(key)?.abort();
      const controller = new AbortController();
      controllers.current.set(key, controller);
      setLoading((s) => withSet(s, key));
      setFailed((m) => {
        if (!m.has(key)) return m;
        const next = new Map(m);
        next.delete(key);
        return next;
      });
      warehouseApi
        .listGoodsIssueSources(
          goodsIssueID,
          { sku, storageID },
          { signal: controller.signal }
        )
        .then((data) => {
          if (controller.signal.aborted) return;
          setCandidates((prev) => {
            const next = new Map(prev);
            data.forEach((c) => next.set(c.stockID, c));
            return next;
          });
          setLists((prev) =>
            new Map(prev).set(key, new Set(data.map((c) => c.stockID)))
          );
        })
        .catch((err) => {
          if (controller.signal.aborted || isAbortError(err)) return;
          setFailed((m) =>
            new Map(m).set(key, parseWarehouseError(err).message)
          );
        })
        .finally(() => {
          if (controller.signal.aborted) return;
          setLoading((s) => withoutSet(s, key));
          if (controllers.current.get(key) === controller) {
            controllers.current.delete(key);
          }
        });
    },
    [goodsIssueID]
  );

  // A row that points at a stock row nobody described yet (a starting line).
  const wantedKey = Array.from(
    new Set(
      rows
        .filter(
          (r) => r.sku && r.storageID && r.stockID && !candidates.has(r.stockID)
        )
        .map((r) => sourceKey(r.sku, r.storageID))
    )
  )
    .sort()
    .join(KEY_SEP);
  useEffect(() => {
    if (!wantedKey) return;
    wantedKey.split(KEY_SEP).forEach((key) => {
      if (requested.current.has(key)) return;
      const [sku, storageID] = key.split('\u0001');
      load(sku, storageID);
    });
  }, [wantedKey, load]);

  useEffect(() => {
    const live = controllers.current;
    const asked = requested.current;
    return () => {
      live.forEach((c) => c.abort());
      live.clear();
      // StrictMode re-mount: ask again instead of waiting on aborted calls.
      asked.clear();
    };
  }, []);

  /** A batch the user picked: known now, and part of its location's list. */
  const remember = useCallback((sku: string, c: GoodsIssueSource) => {
    setCandidates((prev) => new Map(prev).set(c.stockID, c));
    setLists((prev) => {
      const key = sourceKey(sku, c.storageID);
      const list = prev.get(key);
      return !list || list.has(c.stockID)
        ? prev
        : new Map(prev).set(key, withSet(list, c.stockID));
    });
  }, []);

  /** Stock moved under us: ask again for these rows and restart the pickers. */
  const refresh = useCallback(
    (targets: readonly EditRow[]) => {
      new Set(
        targets
          .filter((r) => r.sku && r.storageID)
          .map((r) => sourceKey(r.sku, r.storageID))
      ).forEach((key) => {
        const [sku, storageID] = key.split('\u0001');
        load(sku, storageID);
      });
      setVersion((v) => v + 1);
    },
    [load]
  );

  const known = useMemo<SourceKnowledge>(
    () => ({ candidates, lists }),
    [candidates, lists]
  );
  return { known, loading, failed, version, remember, refresh, reload: load };
};

/* --------------------------------- view ---------------------------------- */

const FIELD_LABEL: Record<RowField, string> = {
  sku: 'SKU',
  storageID: 'lokasi',
  stockID: 'batch',
  qty: 'qty',
};

/** Codes after which the document is not what edit mode opened on. */
const STALE = new Set(['INVALID_STATUS_TRANSITION', 'NOT_FOUND']);

const FieldMessage: React.FC<{ message?: string | null; testId?: string }> = ({
  message,
  testId,
}) =>
  message ? (
    <p
      className="mt-1 text-[11px] leading-snug text-(--state-error-base)"
      data-testid={testId}
    >
      {message}
    </p>
  ) : null;

interface Props {
  goodsIssueID: string;
  /** The goods issue's current lines (the rows start from them). */
  lines: GoodsIssueLine[];
  /** Ordered SKUs; per-SKU totals must end equal to these. */
  ordered: OrderedSku[];
  /** The PUT is running. */
  saving: boolean;
  /** Sends the PUT; rejects with the server error (422s land on rows). */
  onSave: (body: UpdateGoodsIssueLinesPayload) => Promise<void>;
  onCancel: () => void;
  /** The goods issue changed under us (409 / 404): reload + leave edit mode. */
  onStale: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Saving confirms the pick (Not picked → Picked), so Simpan is allowed on
   * an unchanged list. Otherwise it needs a change.
   */
  confirmsPick?: boolean;
}

/**
 * "Edit picked items": the whole reservation list as editable rows — SKU
 * (from the order) · location · batch · qty — modelled on the eFishery WMS
 * picking editor. One SKU can be split across locations / batches; totals per
 * SKU must equal the order before Simpan. Simpan replaces the list
 * (PUT /goods-issues/:id/lines) in one go.
 */
const EditablePickingTable: React.FC<Props> = ({
  goodsIssueID,
  lines,
  ordered,
  saving,
  onSave,
  onCancel,
  onStale,
  onDirtyChange,
  confirmsPick = false,
}) => {
  const [initial] = useState(() => rowsFromLines(lines));
  const [rows, setRows] = useState<EditRow[]>(initial);
  const [serverErrors, setServerErrors] = useState<Map<number, RowErrors>>(
    () => new Map()
  );
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const sources = useSourceLookups(goodsIssueID, rows);
  // Names of the locations picked in this session (the picker's own page may
  // no longer hold them).
  const [pickedLabels, setPickedLabels] = useState<ReadonlyMap<string, string>>(
    () => new Map()
  );
  const orderedBy = useMemo(
    () => new Map(ordered.map((o) => [o.sku, o])),
    [ordered]
  );

  const check = useMemo(
    () => checkRows(rows, ordered, sources.known),
    [rows, ordered, sources.known]
  );
  const dirty = useMemo(() => isDirty(initial, rows), [initial, rows]);
  const savable = dirty || confirmsPick;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const orderedSkus = useMemo(() => ordered.map((o) => o.sku), [ordered]);

  const update = (key: number, edit: (r: EditRow) => EditRow) => {
    setRows((prev) => prev.map((r) => (r.key === key ? edit(r) : r)));
    setServerErrors((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
    setFormErrors([]);
  };

  const remove = (key: number) => {
    setRows((prev) =>
      prev.length > 1 ? prev.filter((r) => r.key !== key) : prev
    );
    setServerErrors((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
    setFormErrors([]);
  };

  const add = () => {
    setRows((prev) => [...prev, newEditRow()]);
    setFormErrors([]);
  };

  const errorOf = (i: number, key: number, field: RowErrorKey): string | null =>
    serverErrors.get(key)?.[field] ??
    (field === 'row' ? null : check.rows[i]?.errors[field as RowField]) ??
    null;

  const save = async () => {
    if (saving || !check.canSave || !savable) return;
    setServerErrors(new Map());
    setFormErrors([]);
    const sent = rows;
    try {
      await onSave(toPayload(sent));
    } catch (err) {
      const e = parseWarehouseError(err);
      const { list, message } = fieldErrorsOf(err);
      const mapped = mapServerErrors(list, sent);
      if (mapped.byKey.size || mapped.form.length) {
        setServerErrors(mapped.byKey);
        setFormErrors(mapped.form);
        // Stock moved since the candidates loaded: show fresh availability.
        if (e.code === 'INSUFFICIENT_STOCK') {
          sources.refresh(sent.filter((r) => mapped.byKey.has(r.key)));
        }
        return;
      }
      toast.error(message);
      if (
        e.status === 409 ||
        e.status === 404 ||
        (e.code !== null && STALE.has(e.code))
      ) {
        onStale();
      }
    }
  };

  const cancel = () => {
    if (dirty) setConfirmDiscard(true);
    else onCancel();
  };

  /* ------------------------------- summary ------------------------------- */

  const issues: string[] = [
    ...formErrors,
    ...check.incomplete.map(
      (i) =>
        `Baris ${i + 1} belum lengkap: ${check.rows[i].missing
          .map((f) => FIELD_LABEL[f])
          .join(', ')}.`
    ),
  ];
  const rowsWithErrors = rows.flatMap((r, i) =>
    serverErrors.has(r.key) || check.invalid.includes(i) ? [i + 1] : []
  );
  if (rowsWithErrors.length) {
    issues.push(`Perbaiki baris ${rowsWithErrors.join(', ')}.`);
  }
  const anyOver = check.totals.some((t) => t.diff > 0);

  return (
    <div className="flex flex-col gap-3" data-testid="picking-edit">
      <Alert
        status={check.totalsOk ? 'success' : anyOver ? 'error' : 'warning'}
        appearance="stroke"
        size="lg"
        title={
          check.totalsOk
            ? 'Total per SKU sudah sesuai order'
            : 'Total per SKU harus sama dengan qty order'
        }
        data-testid="picking-edit-totals"
      >
        <ul className="flex flex-col gap-0.5">
          {check.totals.map((t) => (
            <li
              key={t.sku}
              className="font-mono text-[12px] tabular-nums"
              data-testid={`picking-edit-total-${t.sku}`}
              data-state={t.diff === 0 ? 'ok' : t.diff < 0 ? 'short' : 'over'}
            >
              {t.sku}: {fmtQty(t.total)} / {fmtQty(t.ordered)}{' '}
              {t.diff === 0 ? (
                <i
                  className="ri-check-line text-(--state-success-base)"
                  aria-label="sesuai"
                />
              ) : (
                <span
                  className={
                    t.diff < 0
                      ? 'text-(--state-warning-dark)'
                      : 'text-(--state-error-base)'
                  }
                >
                  ({t.diff < 0 ? 'kurang' : 'lebih'} {fmtQty(Math.abs(t.diff))})
                </span>
              )}
            </li>
          ))}
        </ul>
      </Alert>

      {issues.length > 0 && (
        <Alert
          status={
            formErrors.length || rowsWithErrors.length ? 'error' : 'warning'
          }
          appearance="stroke"
          size="sm"
          data-testid="picking-edit-issues"
        >
          {issues.join(' ')}
        </Alert>
      )}

      <div className="overflow-hidden rounded-lg border border-stroke-soft-200 bg-bg-white-0">
        <Table containerClassName="overflow-x-auto">
          <TableHeader>
            <TableRow>
              {[
                ['SKU', 'min-w-[170px]'],
                ['Product name', 'min-w-[120px]'],
                ['Item location', 'min-w-[240px]'],
                ['Batch number', 'min-w-[260px]'],
                ['Available', 'text-right'],
                ['Qty', 'text-right'],
                ['UoM', ''],
              ].map(([title, cls]) => (
                <TableHead
                  key={title}
                  className={cn('whitespace-nowrap text-[11px]', cls)}
                >
                  {title}
                </TableHead>
              ))}
              <TableHead className="w-px">
                <span className="sr-only">Hapus</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => {
              const n = i + 1;
              const key = sourceKey(row.sku, row.storageID);
              const failed = sources.failed.get(key);
              const cand = candidateOf(sources.known, row);
              const info = orderedBy.get(row.sku);
              const skuErr = errorOf(i, row.key, 'sku');
              const storageErr = errorOf(i, row.key, 'storageID');
              const stockErr = errorOf(i, row.key, 'stockID');
              const qtyErr = errorOf(i, row.key, 'qty');
              const rowErr = errorOf(i, row.key, 'row');
              return (
                <TableRow
                  key={row.key}
                  className="align-top hover:bg-transparent"
                  data-testid={`picking-edit-row-${i}`}
                >
                  <TableCell className="px-3 py-2">
                    <OrderedSkuSelect
                      aria-label={`SKU baris ${n}`}
                      skus={orderedSkus}
                      value={row.sku}
                      invalid={!!skuErr}
                      disabled={saving}
                      onPick={(v) => update(row.key, (r) => withSku(r, v))}
                    />
                    <FieldMessage message={skuErr} />
                    <FieldMessage message={rowErr} />
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    <span className={nameCls}>{info?.itemName ?? '—'}</span>
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    <SourceStorageSelect
                      aria-label={`Lokasi baris ${n}`}
                      goodsIssueID={goodsIssueID}
                      sku={row.sku}
                      value={row.storageID}
                      selectedLabel={locationLabelOf(
                        row,
                        pickedLabels,
                        sources.known
                      )}
                      placeholder={row.sku ? 'Pilih lokasi' : 'Pilih SKU dulu'}
                      invalid={!!storageErr}
                      disabled={saving || !row.sku}
                      version={sources.version}
                      onPick={(s) => {
                        setPickedLabels((m) =>
                          new Map(m).set(
                            s.storageID,
                            s.storagePath || s.storageName
                          )
                        );
                        update(row.key, (r) => withStorage(r, s.storageID));
                      }}
                    />
                    {failed && (
                      <div
                        className="mt-1 flex flex-wrap items-center gap-1"
                        data-testid={`picking-edit-sources-error-${i}`}
                      >
                        <span className="text-[11px] text-(--state-error-base)">
                          {failed}
                        </span>
                        <Button
                          tone="neutral"
                          style="link"
                          size="xs"
                          className="h-auto px-0 py-0 text-[11px]"
                          onClick={() => sources.reload(row.sku, row.storageID)}
                        >
                          Coba lagi
                        </Button>
                      </div>
                    )}
                    <FieldMessage message={storageErr} />
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    <SourceBatchSelect
                      aria-label={`Batch baris ${n}`}
                      goodsIssueID={goodsIssueID}
                      sku={row.sku}
                      storageID={row.storageID}
                      value={row.stockID}
                      selectedLabel={batchLabelOf(row, sources.known)}
                      placeholder={
                        row.storageID ? 'Pilih batch' : 'Pilih lokasi dulu'
                      }
                      invalid={!!stockErr}
                      disabled={saving || !row.storageID}
                      version={sources.version}
                      onPick={(c) => {
                        sources.remember(row.sku, c);
                        update(row.key, (r) => withStock(r, c.stockID));
                      }}
                    />
                    <FieldMessage
                      message={stockErr}
                      testId={`picking-edit-stock-error-${i}`}
                    />
                  </TableCell>
                  <TableCell className="px-3 py-2 text-right">
                    <span
                      className={numCls}
                      data-testid={`picking-edit-available-${i}`}
                    >
                      {cand ? fmtQty(cand.available) : '—'}
                    </span>
                    {cand && cand.heldByThisIssue > 0 && (
                      <p className={cn(metaCls, 'whitespace-nowrap')}>
                        {fmtQty(cand.heldByThisIssue)} di picking ini
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    <div className="ml-auto w-24">
                      <TextInput
                        aria-label={`Qty baris ${n}`}
                        type="number"
                        size="sm"
                        min={0.01}
                        step={0.01}
                        inputMode="decimal"
                        value={row.qty}
                        invalid={!!qtyErr}
                        disabled={saving || !row.sku}
                        onChange={(v) => update(row.key, (r) => withQty(r, v))}
                        className="text-right"
                      />
                    </div>
                    <FieldMessage
                      message={qtyErr}
                      testId={`picking-edit-qty-error-${i}`}
                    />
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    <span className={metaCls}>{info?.uomName ?? '—'}</span>
                  </TableCell>
                  <TableCell className="px-2 py-2">
                    {rows.length > 1 && (
                      <Button
                        tone="neutral"
                        style="ghost"
                        size="icon-xs"
                        aria-label={`Hapus baris ${n}`}
                        title="Hapus baris"
                        disabled={saving}
                        onClick={() => remove(row.key)}
                      >
                        <i className="ri-delete-bin-line text-[14px]" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col items-start gap-1">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            data-testid="picking-edit-add"
            disabled={saving || !check.canAdd}
            onClick={add}
            leftIcon={<i className="ri-add-line text-[14px]" />}
          >
            Tambah baris
          </Button>
          {!check.canAdd && (
            <DisabledReason testId="picking-edit-add-reason">
              {rows.length >= MAX_EDIT_ROWS
                ? `Maksimal ${MAX_EDIT_ROWS} baris`
                : 'Semua SKU sudah sesuai qty order'}
            </DisabledReason>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {(!savable || !check.canSave) && !saving && (
            <DisabledReason testId="picking-edit-save-reason">
              {!savable
                ? 'Belum ada perubahan'
                : 'Lengkapi baris & samakan total per SKU'}
            </DisabledReason>
          )}
          <Button
            tone="neutral"
            style="stroke"
            data-testid="picking-edit-cancel"
            disabled={saving}
            onClick={cancel}
          >
            Batal
          </Button>
          <Button
            data-testid="picking-edit-save"
            loading={saving}
            disabled={!check.canSave || !savable}
            onClick={() => void save()}
            leftIcon={<i className="ri-save-line text-[14px]" />}
          >
            Simpan
          </Button>
        </div>
      </div>

      <ActionDialog
        open={confirmDiscard}
        title="Buang perubahan"
        description="Perubahan picking list yang belum disimpan akan hilang."
        confirmLabel="Buang perubahan"
        tone="destructive"
        testId="picking-edit-discard"
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          onCancel();
        }}
      />
    </div>
  );
};

export default EditablePickingTable;
